import React, { useState, useRef, useEffect } from 'react';
import { Button, Loader, Dimmer, Segment, Divider, Container } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Dialog } from 'primereact/dialog';
import { Steps } from 'primereact/steps';
import { Typography } from '@material-ui/core';
import messages, { FormattedMessage } from 'shared/messages/messages';
import ReactCrop from 'react-image-crop';
import modalStyles from 'shared/components/modalStyles';
import 'react-image-crop/dist/ReactCrop.css'

import {
    getKnockOutElements,
    uploadMoment,
    backupMoment,
    processThumbnails,
    validateKnockoutElements
} from 'app/actions/kioskActions';

import {
    setDraggableImage,
    showCropMoment,
    showPrintSizeEdits
} from 'app/actions/photoSwipeActions'


const styles = () => ({
    inputGroup: {
        padding: '0.3rem 0'
    },
    selectionContainer: {
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        justifyContent: 'center',
        rowGap: '10px',
        columnGap: '3em',
    },
    selection: {},
    selectionItem: {
        margin: 'auto',
    },
    printCountOption: {
        width: '3em',
        textAlign: 'center'
    },
    editorContainer: {
        height: '50vh'
    },
    bgEditor: {
        textAlign: 'center'
    },
    ...modalStyles()
});

const ImageCropDialog = (props) => {
    const { cropMomentVisible, onClose, cropMomentId, classes,
        getKnockOutElements, knockOutElements, setDraggableImage, printSizes, showCropMoment,
        selectedPrintSizeForCrop, showPrintSizeEdits, cropMoment } = props;

    const [loaded, setLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [crop, setCrop] = useState({ aspect: 1 });
    const [subjectCrop, setSubjectCrop] = useState({ aspect: 1 });
    const [overlayCrop, setOverlayCrop] = useState({ aspect: 1 });

    const [croppedSubjImg, setCroppedSubjImg] = useState(null);
    const [croppedOverlayjImg, setCroppedOverlayImg] = useState(null);

    const [subjCropReady, setSubjCropReady] = useState(false);
    const [overlayCropReady, setOverlayCropReady] = useState(false);

    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedPrintSize, setSelectedPrintSize] = useState(null);
    const [nextText, setNextText] = useState('Next');
    const [cropAspect, setCropAspect] = useState(0);

    const bgRef = useRef(null);
    const overlayImgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    useEffect(() => {
        if (!loaded && cropMomentId > 0) {
            const nextLabel = <FormattedMessage {...messages.kiosk_edit_moment_next} />;
            setNextText(nextLabel);
            setLoaded(true);
            setDraggableImage(true);
        }
    }, [cropMomentId, selectedPrintSizeForCrop])

    useEffect(() => {
        if (selectedPrintSizeForCrop) {
            getKnockOutElements(cropMomentId, selectedPrintSizeForCrop?.size);
            setSelectedPrintSize(selectedPrintSizeForCrop);

            if (cropMoment && cropMoment.w > cropMoment.h) {
                setCropAspect(selectedPrintSizeForCrop.print_width / selectedPrintSizeForCrop.print_height);
            } else {
                setCropAspect(selectedPrintSizeForCrop.print_height / selectedPrintSizeForCrop.print_width);
            }
        }
    }, [selectedPrintSizeForCrop])

    const uploadFinalImage = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(croppedOverlayjImg);
            if (!response.ok) throw new Error('Could not read the edited image');
            const blob = await response.blob();
            const printSize = selectedPrintSize?.size;
            const elements = await props.validateKnockoutElements(cropMomentId, printSize);
            const file = new File([blob], 'edit.jpeg', { type: 'image/jpeg' });
            await props.uploadMoment(elements.new_moment_id, 'image/jpeg', file, cropMomentId, printSize);
            await props.processThumbnails(elements.new_moment_id, cropMomentId, printSize);
            setNextText(<FormattedMessage {...messages.kiosk_edit_moment_done} />);
        } catch (error) {
            window.alert('Unable to save this edit. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }

    const getElement = (url) => {
        return fetch(url, { credentials: 'include' })
    }

    const generateCroppedOverlayImage = async () => {
        if (!bgRef.current)
            return;

        const bgImage = new Image();
        const subjImage = new Image();
        const overlayImage = new Image();
        const overlayAvailable = knockOutElements.overlay_id !== null;

        const canvas = previewCanvasRef.current;
        const subjCrop = subjectCrop;

        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio;

        ctx.imageSmoothingQuality = 'high';
        ctx.imageSmoothingEnabled = true;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

        // bgImage.crossOrigin = 'anonymous';
        // subjImage.crossOrigin = 'anonymous';
        // overlayImage.crossOrigin = 'anonymous';

        let bgBlob = await getElement(knockOutElements.background_url).then(r => r.blob());
        bgImage.src = URL.createObjectURL(bgBlob);

        bgImage.onload = async () => {

            // Getting percentage difference of the rendered BG vs original BG image
            // The background crop values will then be multiplied by the resultant scale
            // to adjust the x/y/width/height values accordingly so that what the user selected
            // at a smaller scale can be pull from the original at its larger scale
            const bgViewHeight = 300;

            let bgViewScale = bgImage.height / bgViewHeight;

            // Setting Canvas size equal to that of the background
            canvas.height = bgImage.height;
            canvas.width = bgImage.width;

            let subjBlob = await getElement(knockOutElements.orig_moment_subject_url).then(r => r.blob());
            subjImage.src = URL.createObjectURL(subjBlob);

            subjImage.onload = async () => {
                // Determining scale of subject to view height to calculate
                // Higher res version
                let subjScale = subjCrop.height / bgViewHeight;

                // Height = percentage of viewable background height vs crop height * canvas height
                let subjHeight = bgImage.height * subjScale;

                // Final width locked to aspect ratio
                let subjAspect = subjImage.width / subjImage.height;
                let subjWidth = subjAspect * subjHeight;

                const subjX = (subjCrop.x * bgViewScale) - (150 * bgViewScale);
                const subjY = (subjCrop.y * bgViewScale) - (150 * bgViewScale);

                if (!overlayAvailable) {
                    ctx.drawImage(
                        bgImage,
                        0,
                        0,
                        bgImage.width,
                        bgImage.height,
                    );

                    ctx.drawImage(
                        subjImage,
                        subjX,
                        subjY,
                        subjWidth,
                        subjHeight
                    );

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            return;
                        }

                        const url = URL.createObjectURL(blob);
                        setCroppedOverlayImg(url);
                        setActiveIndex(activeIndex + 1);
                        setIsLoading(false);
                    }, 'image/jpeg');

                    return;
                }

                let overlayBlob = await getElement(knockOutElements.overlay_url).then(r => r.blob());
                overlayImage.src = URL.createObjectURL(overlayBlob);

                overlayImage.onload = function () {
                    setIsLoading(true);

                    // Determining scale of overlay heigh to view height to calculate
                    // Higher res version
                    let overlayScale = overlayCrop.height / bgViewHeight;

                    // Height = percentage of viewable background height vs crop height * canvas height
                    let overlayHeight = bgImage.height * overlayScale;

                    // Final width locked to aspect ratio
                    let overlayAspect = overlayImage.width / overlayImage.height;
                    let overlayWidth = overlayAspect * overlayHeight;

                    const overlayCropX = (overlayCrop.x * bgViewScale) - (150 * bgViewScale);
                    const overlayCropY = (overlayCrop.y * bgViewScale) - (150 * bgViewScale);

                    ctx.drawImage(
                        bgImage,
                        0,
                        0,
                        bgImage.width,
                        bgImage.height,
                    );

                    ctx.drawImage(
                        subjImage,
                        subjX,
                        subjY,
                        subjWidth,
                        subjHeight
                    );

                    ctx.drawImage(
                        overlayImage,
                        overlayCropX,
                        overlayCropY,
                        overlayWidth,
                        overlayHeight
                    );

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            return;
                        }

                        const url = URL.createObjectURL(blob);
                        setCroppedOverlayImg(url);
                        setActiveIndex(activeIndex + 1);
                        setIsLoading(false);
                    }, 'image/jpeg');
                }
            }
        };
    };


    const generateCroppedImageAndOverlayOnly = async () => {

        const bgImage = new Image();
        const overlayImage = new Image();

        const canvas = previewCanvasRef.current;

        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio;

        ctx.imageSmoothingQuality = 'high';
        ctx.imageSmoothingEnabled = true;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

        // bgImage.crossOrigin = 'anonymous';
        // overlayImage.crossOrigin = 'anonymous';

        let bgBlob = await getElement(knockOutElements.overlay_orig_moment_url).then(r => r.blob());
        bgImage.src = URL.createObjectURL(bgBlob);

        bgImage.onload = async () => {

            const bgViewHeight = 300;

            let bgViewScale = bgImage.height / bgViewHeight;

            let imgHeight = crop.height * bgViewScale;
            let imgWidth = crop.width * bgViewScale;

            const imgCropX = (crop.x * bgViewScale);
            const imgCropY = (crop.y * bgViewScale);

            canvas.width = imgWidth;
            canvas.height = imgHeight;

            let overlayBlob = await getElement(knockOutElements.overlay_url).then(r => r.blob());
            overlayImage.src = URL.createObjectURL(overlayBlob);

            overlayImage.onload = function () {
                setIsLoading(true);

                // Determining scale of overlay heigh to view height to calculate
                // Higher res version
                let overlayScale = overlayCrop.height / bgViewHeight;

                // Height = percentage of viewable background height vs crop height * canvas height
                let overlayHeight = canvas.height * overlayScale;

                // Final width locked to aspect ratio
                let overlayAspect = overlayImage.width / overlayImage.height;
                let overlayWidth = overlayAspect * overlayHeight;

                let imgViewScale = imgHeight / bgViewHeight;

                const overlayCropX = (overlayCrop.x * imgViewScale) - (150 * imgViewScale);
                const overlayCropY = (overlayCrop.y * imgViewScale) - (150 * imgViewScale);

                ctx.drawImage(
                    bgImage,
                    imgCropX,
                    imgCropY,
                    imgWidth,
                    imgHeight,
                    0,
                    0,
                    imgWidth,
                    imgHeight
                );

                ctx.drawImage(
                    overlayImage,
                    overlayCropX,
                    overlayCropY,
                    overlayWidth,
                    overlayHeight
                );

                canvas.toBlob((blob) => {
                    if (!blob) {
                        return;
                    }

                    const url = URL.createObjectURL(blob);
                    setCroppedOverlayImg(url);
                    setActiveIndex(activeIndex + 1);
                    setIsLoading(false);
                }, 'image/jpeg');
            }
        }

    };

    const generateCroppedSubjImage = async () => {
        if (!bgRef.current)
            return;

        const bgImage = new Image();
        const subjImage = new Image();
        const overlayImage = new Image();

        const canvas = previewCanvasRef.current;
        const subjCrop = subjectCrop;

        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio;

        ctx.imageSmoothingQuality = 'high';
        ctx.imageSmoothingEnabled = true;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

        // bgImage.crossOrigin = 'anonymous';
        // subjImage.crossOrigin = 'anonymous';
        // overlayImage.crossOrigin = 'anonymous';

        let bgBlob = await getElement(knockOutElements.background_url).then(r => r.blob()).catch((ex) => {
            console.log(ex);
        });
        bgImage.src = URL.createObjectURL(bgBlob);

        bgImage.onload = async () => {

            // Getting percentage difference of the rendered BG vs original BG image
            // The background crop values will then be multiplied by the resultant scale
            // to adjust the x/y/width/height values accordingly so that what the user selected
            // at a smaller scale can be pull from the original at its larger scale
            const bgHeight = 300;

            const aspectRatio = bgImage.width / bgImage.height;
            var bgWidth;

            if (aspectRatio > 1) {
                bgWidth = bgHeight * aspectRatio;
            } else {
                bgWidth = bgImage.width;
            }

            canvas.height = bgHeight;
            canvas.width = bgWidth;

            // Need to update size of bgImage so it fits in the preview canvas
            bgImage.height = bgHeight;
            bgImage.width = bgWidth;

            let subjBlob = await getElement(knockOutElements.orig_moment_subject_url).then(r => r.blob());
            subjImage.src = URL.createObjectURL(subjBlob);

            subjImage.onload = async () => {

                let overlayBlob = await getElement(knockOutElements.overlay_url).then(r => r.blob());
                overlayImage.src = URL.createObjectURL(overlayBlob);

                overlayImage.onload = function () {
                    setIsLoading(true);

                    ctx.drawImage(
                        bgImage,
                        0,
                        0,
                        bgImage.width,
                        bgImage.height,
                    );

                    ctx.drawImage(
                        subjImage,
                        (subjCrop.x - 150),
                        (subjCrop.y - 150),
                        subjCrop.width,
                        subjCrop.height
                    );

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            return;
                        }

                        const url = URL.createObjectURL(blob);
                        setCroppedSubjImg(url);
                        setActiveIndex(activeIndex + 1);
                        setIsLoading(false);
                    }, 'image/jpeg');
                }
            }
        };

    };

    const generateCroppedImage = async () => {
        if (!crop || !previewCanvasRef.current) {
            return;
        }

        const image = new Image();
        const canvas = previewCanvasRef.current;
        const backgroundAvaible = knockOutElements.background_id !== null;
        const overlayAvailable = knockOutElements.overlay_id !== null;
        const isRegularImage = !backgroundAvaible && !overlayAvailable;

        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio;

        ctx.imageSmoothingQuality = 'high';
        ctx.imageSmoothingEnabled = true;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);


        // Ensure this image has appropriate CORS headers
        // This is important for cross-origin images
        // image.crossOrigin = 'anonymous';

        const momenturl = isRegularImage ? knockOutElements.regular_moment_url : knockOutElements.overlay_orig_moment_url;
        const origBlob = await getElement(momenturl).then(r => r.blob());

        image.src = URL.createObjectURL(origBlob);

        image.onload = () => {
            setIsLoading(true);

            const bgViewHeight = 300;
            let bgViewScale = image.height / bgViewHeight;

            let imgHeight = crop.height * bgViewScale;
            let imgWidth = crop.width * bgViewScale;

            const imgCropX = (crop.x * bgViewScale);
            const imgCropY = (crop.y * bgViewScale);

            canvas.width = imgWidth;
            canvas.height = imgHeight;

            ctx.drawImage(
                image,
                imgCropX,
                imgCropY,
                imgWidth,
                imgHeight,
                0,
                0,
                canvas.width,
                canvas.height
            );

            canvas.toBlob((blob) => {
                if (!blob) {
                    return;
                }

                const url = URL.createObjectURL(blob);
                if (isRegularImage) {
                    setCroppedOverlayImg(url);
                } else {
                    setCroppedSubjImg(url);
                }

                setActiveIndex(activeIndex + 1);
                setIsLoading(false);
            }, 'image/jpeg');
        };
    };

    const closeModal = (reload) => {
        const nextLabel = <FormattedMessage {...messages.kiosk_edit_moment_next} />;
        setNextText(nextLabel);
        setLoaded(false);
        setSubjCropReady(false);
        setSubjectCrop({ aspect: 1 });
        setActiveIndex(0);
        onClose(reload);
    }

    const confirmClose = () => {
        const doneLabel = <FormattedMessage {...messages.kiosk_edit_moment_done} />;
        const nextLabel = <FormattedMessage {...messages.kiosk_edit_moment_next} />;
        if (nextText?.props?.id !== doneLabel?.props?.id) {
            let question = messages.kiosk_edit_moment_close_confirmation.defaultMessage;
            if (window.confirm(question)) {
                closeModal(false);
                setNextText(nextLabel);
            };
        } else {
            closeModal(false);
            setNextText(nextLabel);
        }
    }


    var items = [{
        label: <FormattedMessage {...messages.kiosk_edit_moment_complete_edit} />,
        command: (event) => {
        }
    }];

    const backgroundAvaible = knockOutElements.background_id !== null;
    const overlayAvailable = knockOutElements.overlay_id !== null;

    if (overlayAvailable) {
        const applyOverlayLabel = <FormattedMessage {...messages.kiosk_edit_moment_apply_overlay} />;
        const cropOverlayItem = items.find((x) => x.label === applyOverlayLabel);
        if (!cropOverlayItem) {
            items = [
                {
                    label: applyOverlayLabel,
                    command: (event) => {
                    }
                }, ...items];
        }
    }

    if (!backgroundAvaible) {
        const cropImageLabel = <FormattedMessage {...messages.kiosk_edit_moment_crop_image} />;
        const cropImageItem = items.find((x) => x.label === cropImageLabel);
        if (!cropImageItem) {
            items = [{
                label: cropImageLabel,
                command: (event) => {
                }
            }, ...items];
        }
    } else {
        const adjustSubjLabel = <FormattedMessage {...messages.kiosk_edit_moment_adjust_subject} />;
        const cropSubjItem = items.find((x) => x.label === adjustSubjLabel);
        if (!cropSubjItem) {
            items = [
                {
                    label: adjustSubjLabel,
                    command: (event) => {
                    }
                }, ...items];
        }
    }

    const dialogFooter = () => {
        const applyChangesLabel = <FormattedMessage {...messages.kiosk_edit_moment_apply_changes} />;
        const nextLabel = <FormattedMessage {...messages.kiosk_edit_moment_next} />;
        const backLabel = <FormattedMessage {...messages.kiosk_edit_moment_back} />;
        const doneLabel = <FormattedMessage {...messages.kiosk_edit_moment_done} />;
        return <div>
            <Divider />
            <Segment>
                {
                    activeIndex > 0 &&
                    <Button onClick={async () => {
                        if (activeIndex > 0) {
                            setActiveIndex(activeIndex - 1);
                        }
                        setNextText(nextLabel);
                    }}>{backLabel}</Button>
                }
                <Button primary onClick={async () => {
                    if (nextText?.props?.id === doneLabel?.props?.id) {
                        closeModal(true);
                        return;
                    }
                    switch (activeIndex) {
                        case 0:
                            setNextText(nextLabel);
                            if (!backgroundAvaible) {
                                generateCroppedImage();
                            } else {
                                if (!overlayAvailable) {
                                    generateCroppedOverlayImage();
                                } else {
                                    generateCroppedSubjImage();
                                }
                            }
                            if (!backgroundAvaible && !overlayAvailable && !isLoading) {
                                setNextText(applyChangesLabel);
                            }

                            if (backgroundAvaible && !overlayAvailable && !isLoading) {
                                setNextText(applyChangesLabel);
                            }
                            break;
                        case 1:
                            if (!backgroundAvaible) {
                                if (!overlayAvailable) {
                                    uploadFinalImage();
                                } else {
                                    generateCroppedImageAndOverlayOnly();
                                }
                            } else if (!overlayAvailable) {
                                uploadFinalImage();

                            } else {
                                generateCroppedOverlayImage();
                            }
                            break;
                        case 2:
                            uploadFinalImage();
                            break;
                    }
                }}>{nextText}</Button>
            </Segment>
        </div>
    }

    const dialogHeader = () => {
        return <div>
            <label><FormattedMessage {...messages.kiosk_selected_print_size} /></label>
            <Typography variant='h4'>{selectedPrintSizeForCrop?.size}</Typography>
        </div>
    }

    return (
        <div>
            <Dialog header={dialogHeader} visible={cropMomentVisible} style={{ width: '80vw' }} footer={dialogFooter} onHide={confirmClose}>
                <Dimmer active={isLoading}>
                    <Loader>
                        <FormattedMessage {...messages.kiosk_processing_edited_moment} />
                    </Loader>
                </Dimmer>
                <div>
                    <Container>
                        <Steps model={items} activeIndex={activeIndex} readOnly={false} />
                    </Container>
                    {
                        <div className="image-container">
                            <div className={classes.editorContainer}>
                                {
                                    (activeIndex === 0 && !backgroundAvaible) &&
                                    <div className={classes.bgEditor}>
                                        <ReactCrop
                                            aspect={cropAspect}
                                            crop={crop} onChange={c => {
                                                setCrop(c);
                                            }}>
                                            <div>
                                                {
                                                    !overlayAvailable && !backgroundAvaible ?
                                                        <img ref={bgRef} alt='bg_image' src={knockOutElements.regular_moment_url} height={'300px'} /> :
                                                        <img ref={bgRef} alt='bg_image' src={knockOutElements.overlay_orig_moment_url} height={'300px'} />
                                                }
                                            </div>
                                        </ReactCrop>
                                    </div>
                                }
                                {
                                    ((activeIndex === 0 && backgroundAvaible)) &&
                                    <div className={classes.bgEditor}>
                                        <ReactCrop
                                            aspect={knockOutElements.orig_width / knockOutElements.orig_height}
                                            crop={subjectCrop} onChange={c => {
                                                setSubjCropReady(true);
                                                setSubjectCrop(c);
                                            }}>
                                            <div style={{ margin: '150px' }}>
                                                <img ref={bgRef} alt='bg_image' src={knockOutElements.background_url} height={'300px'} />
                                            </div>
                                            {
                                                subjCropReady &&
                                                <div>
                                                    <img src={knockOutElements.orig_moment_subject_url}
                                                        style={{
                                                            position: 'absolute', width: subjectCrop.width, height: subjectCrop.height,
                                                            left: subjectCrop.x, top: subjectCrop.y
                                                        }} />
                                                </div>
                                            }
                                        </ReactCrop>
                                    </div>
                                }
                                {
                                    activeIndex === 1 && overlayAvailable &&
                                    <div className={classes.bgEditor}>
                                        <ReactCrop
                                            aspect={knockOutElements.overlay_width / knockOutElements.overlay_height}
                                            crop={overlayCrop} onChange={c => {
                                                setOverlayCropReady(true);
                                                setOverlayCrop(c);
                                            }}>
                                            <div style={{ margin: '150px' }}>
                                                <img ref={bgRef} alt='bg_image' src={croppedSubjImg} height={'300px'} />
                                            </div>
                                            {
                                                overlayCropReady &&
                                                <div>
                                                    <img src={knockOutElements.overlay_url}
                                                        style={{
                                                            position: 'absolute', width: overlayCrop.width, height: overlayCrop.height,
                                                            left: overlayCrop.x, top: overlayCrop.y
                                                        }} />
                                                </div>
                                            }
                                        </ReactCrop>
                                    </div>
                                }
                                {
                                    (activeIndex === 2 || (activeIndex == 1 && !overlayAvailable)) &&
                                    <div className={classes.bgEditor}>
                                        <div style={{ margin: '150px' }}>
                                            {
                                                <img src={croppedOverlayjImg} height={'300px'} />
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
                <div>
                    <canvas
                        hidden={true}
                        ref={previewCanvasRef}
                    />
                </div>
            </Dialog >
        </div >
    )
}

const mapStateToProps = state => ({
    cropMomentVisible: state.photoSwipeReducer.cropMomentVisible,
    cropMomentSrc: state.photoSwipeReducer.cropMomentSrc,
    cropMomentId: state.photoSwipeReducer.cropMomentId,
    cropMoment: state.photoSwipeReducer.cropMoment,
    knockOutElements: state.kioskReducer.knockOutElements,
    dragImageEnabled: state.photoSwipeReducer.dragImageEnabled,
    user: state.kioskReducer.user,
    printSizes: state.kioskReducer.printSizes,
    validateKnockoutElements: state.kioskReducer.validateKnockoutElements,
    selectedPrintSizeForCrop: state.kioskReducer.selectedPrintSizeForCrop
});


export default connect(mapStateToProps, { getKnockOutElements, setDraggableImage, uploadMoment, backupMoment, processThumbnails, showCropMoment, validateKnockoutElements, showPrintSizeEdits })(withStyles(styles)(ImageCropDialog));
